// src/components/PageFade.jsx
//
// Transição suave entre páginas públicas (home ↔ /social, /noticias ↔
// artigos). Duas peças:
//
//   <PageFade>...</PageFade>  — wrapper que faz fade-in do conteúdo ao montar.
//   useFadeNavigate()         — retorna navigate(to) que primeiro faz fade-out
//                               da página atual e só então troca de rota
//                               (a página de destino faz o próprio fade-in).
//
// Respeita prefers-reduced-motion (sem animação nesse caso).

import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { scrollWindowToTop } from './ScrollToTop';

const FADE_MS = 260;

const FADE_CSS = `
  .page-fade-in { animation: pageFadeIn ${FADE_MS}ms ease both; }
  @keyframes pageFadeIn { from { opacity: 0; } to { opacity: 1; } }
  @media (prefers-reduced-motion: reduce) {
    .page-fade-in { animation: none; }
  }
`;

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export function useFadeNavigate() {
  const navigate = useNavigate();
  return useCallback(
    (to, options) => {
      const el = typeof document !== 'undefined' ? document.getElementById('root') : null;
      if (!el || prefersReducedMotion()) {
        if (to === -1) navigate(-1);
        else navigate(to, options);
        scrollWindowToTop();
        return;
      }
      el.style.transition = `opacity ${FADE_MS}ms ease`;
      el.style.opacity = '0';
      window.setTimeout(() => {
        if (to === -1) navigate(-1);
        else navigate(to, options);
        scrollWindowToTop();
        // Devolve a opacidade: o fade-in fica por conta do wrapper da página
        // de destino (.page-fade-in); aqui só limpamos o estilo inline.
        window.requestAnimationFrame(() => {
          el.style.opacity = '';
          el.style.transition = '';
        });
      }, FADE_MS);
    },
    [navigate]
  );
}

export default function PageFade({ children }) {
  return (
    <>
      <style>{FADE_CSS}</style>
      <div className="page-fade-in">{children}</div>
    </>
  );
}
